import { computed } from 'vue'
import { useReactiveCurrentDate } from '@/shared/composables/useReactiveCurrentDate.js'

/** The active limited-time offer, as serialized by
 *  Subscription::LimitedTimeOfferSerializer. `planIds` empty = every plan. */
export type LimitedTimeOffer = {
  code: string
  percentOff: number
  duration: 'once' | 'forever' | 'repeating'
  durationInMonths: number | null
  barMessage: string
  tagText: string | null
  barPlacement: 'pricing_pages' | 'all_marketing'
  /** Where the exit-intent popup arms; follows_bar inherits barPlacement. */
  exitIntent: 'follows_bar' | 'pricing_pages' | 'all_marketing' | 'off'
  endsAt: string
  planIds: number[]
  /** Real prices of the covered plans, for surfaces with no plans prop. */
  referencePlans?: { id: number; billingInterval: 'quarterly' | 'annually'; amount: number }[]
}

/** True when the offer discounts this plan (empty planIds = every plan). */
export const offerCoversPlan = (offer: LimitedTimeOffer, planId: number) =>
  offer.planIds.length === 0 || offer.planIds.includes(planId)

// Above this remaining window the bar names the day instead of ticking: a
// clock counting down from nine days creates no urgency, a named day does.
const TICKING_WINDOW_MS = 48 * 60 * 60 * 1000

// Countdown state against the server's `ends_at`. The client never invents
// deadlines: everything derives from that one instant, so the display can
// never disagree with what checkout enforces.
export const useOfferCountdown = (endsAt: () => string | null | undefined) => {
  const { now } = useReactiveCurrentDate()

  const remainingMs = computed(() => {
    const iso = endsAt()
    if (!iso) return 0
    return new Date(iso).getTime() - now.value.getTime()
  })

  const expired = computed(() => remainingMs.value <= 0)
  const ticking = computed(() => !expired.value && remainingMs.value <= TICKING_WINDOW_MS)

  // The named-day deadline, literally true in Pacific time. A midnight-PT
  // deadline reads as the evening it ends ("Friday, Aug 14 · midnight PT",
  // formatted one minute early so Saturday 00:00 reads as Friday); any other
  // instant names its actual PT time instead of pretending it's midnight.
  const dateLabel = computed(() => {
    const iso = endsAt()
    if (!iso) return ''
    const end = new Date(iso)
    const inLA = (date: Date, options: Intl.DateTimeFormatOptions) =>
      new Intl.DateTimeFormat('en-US', { ...options, timeZone: 'America/Los_Angeles' }).format(date)
    const isMidnightPT = inLA(end, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }) === '00:00'
    if (isMidnightPT) {
      return `${inLA(new Date(end.getTime() - 60_000), { weekday: 'long', month: 'short', day: 'numeric' })} · midnight PT`
    }
    return `${inLA(end, { weekday: 'long', month: 'short', day: 'numeric' })} · ${inLA(end, { hour: 'numeric', minute: '2-digit' })} PT`
  })

  const pad = (n: number) => String(n).padStart(2, '0')
  const clock = computed(() => {
    const total = Math.max(0, Math.floor(remainingMs.value / 1000))
    return {
      // Hours run past 24 inside the 48h window (e.g. 47:12:08).
      hours: pad(Math.floor(total / 3600)),
      minutes: pad(Math.floor((total % 3600) / 60)),
      seconds: pad(total % 60),
    }
  })

  return { expired, ticking, dateLabel, clock }
}
